import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { ButtonProps, Divider, LinkTypeMap, Menu, Link as MuiLink, Stack, StackProps, Typography, TypographyProps, useTheme } from '@mui/material';
import React, { ElementType, ReactNode, useState } from 'react';
import { Link, LinkLabelAndDescription, LinkPropsBase, onLinkClick } from '../link';
import {filterStrapiContentArray} from "../../shared/utils";

export type LinkGroupProps = LinkPropsBase & {
    withDropdown?: boolean
    alignLeft?: boolean
    headerComponent?: string
    // forceButton?: boolean
    LinkGroupItemsTypographyProps?: TypographyProps
    LinkGroupHeaderTypographyProps?: TypographyProps
    LinkGroupHeaderLabelToIconMapping?: Record<string, ReactNode>
    LinkGroupHeaderLinkProps?: LinkTypeMap['props']
    LinkGroupItemsStackProps?: StackProps
    onClick?: () => void
}

export type LinkGroupItemsProps = {
    links: LinkPropsBase[] | null | undefined,
    alignLeft?: boolean,
    relativeLinkComponent?: ElementType,
    forceLink?: boolean,
    ButtonProps?: ButtonProps
    LinkProps?: LinkTypeMap['props'],
    LinkGroupItemsTypographyProps?: TypographyProps,
    LinkGroupItemsStackProps?: StackProps
    onClick?: () => void
}

function LinkGroupItems({
    links,
    alignLeft,
    relativeLinkComponent,
    forceLink,
    LinkProps,
    LinkGroupItemsTypographyProps,
    LinkGroupItemsStackProps,
    onClick,
    ButtonProps
}: LinkGroupItemsProps) {
    if (!links || links.length === 0) return <></>
    return <Stack
        direction="column"
        alignItems="flex-start"
        justifyContent="flex-start"
        gap={{ xs: 0, sm: 0, md: 2 }}
        {...LinkGroupItemsStackProps}
    >
        {links.map((link, i) => {
            const linkElement = <Link
                relativeLinkComponent={relativeLinkComponent}
                key={i}
                forceLink={forceLink}
                label={link.label}
                url={link.url}
                LinkProps={LinkProps}
                ButtonProps={ButtonProps}
                onClick={onClick}
                sx={!alignLeft ? {
                    width: { xs: "100%", sm: "100%", md: "inherit" }, // SEO
                    minHeight: { xs: "48px", sm: "48px", md: "inherit" }, // SEO
                    ...LinkProps?.sx,
                } : { ml: 2, ...LinkProps?.sx }}
            />;
            return (
                LinkGroupItemsTypographyProps ? <Typography {...LinkGroupItemsTypographyProps} key={i}>{linkElement}</Typography> : linkElement
            )
        })}
    </Stack>
}

export function LinkGroup({
    label,
    description,
    url,
    links,
    buttonType,
    buttonColor,
    icon,
    withDropdown,
    alignLeft,
    relativeLinkComponent,
    headerComponent,
    requiredPublishType,
    ButtonProps,
    forceLink,
    LinkProps,
    LinkGroupItemsTypographyProps,
    LinkGroupHeaderTypographyProps,
    LinkGroupHeaderLabelToIconMapping,
    LinkGroupHeaderLinkProps,
    LinkGroupItemsStackProps,
    // forceButton,
    onClick
}: LinkGroupProps) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const theme = useTheme();

    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleCloseAndClick = (urlLink: string | undefined | null) => {
        onLinkClick(urlLink, undefined, onClick)
        setAnchorEl(null);
    };

    const filteredLinks = filterStrapiContentArray(requiredPublishType, links)

    const hasSubLinks = filteredLinks && filteredLinks.length > 0
    const showHeaderButton = (url || (hasSubLinks && withDropdown));

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>, urlLink: string | undefined | null) => {
        setAnchorEl(event.currentTarget);
        onLinkClick(urlLink, event, onClick)
    };

    return (
        <Stack gap={{ xs: 0, sm: 0, md: 2 }}>
            {label && <Typography variant={headerComponent as any} {...LinkGroupHeaderTypographyProps as any} component={"div"} sx={!headerComponent ? { fontSize: "1rem", ...LinkGroupHeaderTypographyProps?.sx, pl: alignLeft && !showHeaderButton ? 1 : LinkGroupHeaderTypographyProps?.pl } : { ...LinkGroupHeaderTypographyProps?.sx, ml: alignLeft ? 2 : LinkGroupHeaderTypographyProps?.ml }}>
                {showHeaderButton && <Link buttonType={buttonType} buttonColor={buttonColor} icon={icon} endIcon={withDropdown && filteredLinks && filteredLinks.length > 0 && <KeyboardArrowDown htmlColor={theme.palette.text.disabled} />} labelToIconMapping={LinkGroupHeaderLabelToIconMapping} LinkProps={{ ...LinkGroupHeaderLinkProps, ...LinkProps, underline: 'none' }} forceLink={withDropdown === false || forceLink} sx={{ fontWeight: 'inherit', fontSize: 'inherit', textDecoration: 'none', ...LinkGroupHeaderLinkProps?.sx }} relativeLinkComponent={!hasSubLinks ? relativeLinkComponent : undefined} url={url} label={label} ButtonProps={ButtonProps} description={description} onClick={(e) => {
                    if (withDropdown) handleClick(e, url)
                    else onLinkClick(url, e, onClick)
                }} />}
                {!showHeaderButton ? label : undefined}
            </Typography>}

            {filteredLinks && filteredLinks.length > 0 && (
                withDropdown ? (
                    <Menu
                        id={`menu-${label}`}
                        anchorEl={anchorEl}
                        open={open}
                        disableScrollLock
                        onClose={handleClose}
                        MenuListProps={{
                            'aria-labelledby': `menu-btn-${label}`,
                        }}
                    >
                        <Stack gap={2}
                            divider={<Divider />}
                            sx={{ pl: 2, pr: 2, pb: 1, pt: 1 }}>
                            {filteredLinks.map((link, i) => {
                                const isAbsoluteUrl = link.url && link.url.startsWith("http");
                                const hasSubLinks = link.links && filterStrapiContentArray(requiredPublishType, link.links).length > 0;

                                const [anchorSubEl, setAnchorSubEl] = React.useState<null | HTMLElement>(null);
                                const subMenuOpen = Boolean(anchorSubEl);

                                const handleSubMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
                                    event.stopPropagation();
                                    setAnchorSubEl(event.currentTarget);
                                };

                                const handleSubMenuClose = () => {
                                    setAnchorSubEl(null);
                                };

                                const linkElement = (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        onClick={hasSubLinks ? handleSubMenuClick : undefined}
                                        sx={{ cursor: hasSubLinks ? 'pointer' : 'default', width: '100%' }}
                                    >
                                        <LinkLabelAndDescription
                                            TypographyProps={LinkGroupItemsTypographyProps}
                                            label={link.label}
                                            description={link.description}
                                            onClick={!hasSubLinks ? () => handleCloseAndClick(link.url) : undefined}
                                        />
                                        {hasSubLinks && <KeyboardArrowRight />}
                                    </Stack>
                                );

                                const mainLink = relativeLinkComponent && !isAbsoluteUrl ? (
                                    <MuiLink
                                        component={relativeLinkComponent}
                                        underline='none'
                                        href={!hasSubLinks ? (link.url || undefined) : undefined}
                                        to={!hasSubLinks ? (link.url || undefined) : undefined}
                                        key={i}
                                        sx={{ width: '100%', ...LinkProps?.sx }}
                                        {...LinkProps}
                                    >
                                        {linkElement}
                                    </MuiLink>
                                ) : (
                                    <MuiLink
                                        underline='none'
                                        href={!hasSubLinks ? (link.url || undefined) : undefined}
                                        target={isAbsoluteUrl ? '_blank' : undefined}
                                        key={i}
                                        sx={{ width: '100%', ...LinkProps?.sx }}
                                        {...LinkProps}
                                    >
                                        {linkElement}
                                    </MuiLink>
                                );

                                return (
                                    <React.Fragment key={i}>
                                        {mainLink}
                                        {hasSubLinks && (
                                            <Menu
                                                anchorEl={anchorSubEl}
                                                disableScrollLock
                                                open={subMenuOpen}
                                                onClose={handleSubMenuClose}
                                                anchorOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'right',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                }}
                                                sx={{
                                                    '& .MuiPaper-root': {
                                                        minWidth: '200px',
                                                        marginTop: '-16px',
                                                        marginLeft: '16px'
                                                    }
                                                }}
                                            >
                                                <Stack gap={1}
                                                    divider={<Divider />}
                                                    sx={{ pl: 1.5, pr: 1.5, pb: 0.5, pt: 0.5 }}>
                                                    {filterStrapiContentArray(requiredPublishType, link.links)?.map((subLink, j) => {
                                                        const isSubLinkAbsolute = subLink.url && subLink.url.startsWith("http");
                                                        return relativeLinkComponent && !isSubLinkAbsolute ? (
                                                            <MuiLink
                                                                key={j}
                                                                component={relativeLinkComponent}
                                                                underline='none'
                                                                href={subLink.url || undefined}
                                                                to={subLink.url || undefined}
                                                            >
                                                                <LinkLabelAndDescription
                                                                    TypographyProps={LinkGroupItemsTypographyProps}
                                                                    label={subLink.label}
                                                                    description={subLink.description}
                                                                    onClick={() => {
                                                                        handleSubMenuClose();
                                                                        handleCloseAndClick(subLink.url);
                                                                    }}
                                                                />
                                                            </MuiLink>
                                                        ) : (
                                                            <MuiLink
                                                                key={j}
                                                                underline='none'
                                                                href={subLink.url || undefined}
                                                                target={isSubLinkAbsolute ? '_blank' : undefined}
                                                            >
                                                                <LinkLabelAndDescription
                                                                    TypographyProps={LinkGroupItemsTypographyProps}
                                                                    label={subLink.label}
                                                                    description={subLink.description}
                                                                    onClick={() => {
                                                                        handleSubMenuClose();
                                                                        handleCloseAndClick(subLink.url);
                                                                    }}
                                                                />
                                                            </MuiLink>
                                                        );
                                                    })}
                                                </Stack>
                                            </Menu>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </Stack>
                    </Menu>
                ) : (
                    <>
                        <LinkGroupItems
                            onClick={onClick}
                            LinkGroupItemsStackProps={LinkGroupItemsStackProps}
                            ButtonProps={ButtonProps}
                            LinkGroupItemsTypographyProps={LinkGroupItemsTypographyProps}
                            forceLink={forceLink}
                            LinkProps={LinkProps}
                            links={filteredLinks}
                            alignLeft={alignLeft}
                            relativeLinkComponent={relativeLinkComponent}
                        />
                        {alignLeft && filteredLinks.map((link, i) => {
                            const nestedLinks = filterStrapiContentArray(requiredPublishType, link.links);
                            if (nestedLinks && nestedLinks.length > 0) {
                                return (
                                    <Stack key={i} sx={{ ml: 3 }}>
                                        <LinkGroupItems
                                            onClick={onClick}
                                            LinkGroupItemsStackProps={LinkGroupItemsStackProps}
                                            ButtonProps={ButtonProps}
                                            LinkGroupItemsTypographyProps={LinkGroupItemsTypographyProps}
                                            forceLink={forceLink}
                                            LinkProps={LinkProps}
                                            links={nestedLinks}
                                            alignLeft={alignLeft}
                                            relativeLinkComponent={relativeLinkComponent}
                                        />
                                    </Stack>
                                );
                            }
                            return null;
                        })}
                    </>
                )
            )}
        </Stack>
    )
}
